import { AppMenuSchemaType } from "@dialogs/app-add-edit-dialog/app-add-edit-form.schema";
import { getLinkFromParams } from "@utils/app.util";
import {
  createContext,
  useContext,
  ReactNode,
  useState,
  useMemo,
  Dispatch,
  SetStateAction,
  useCallback,
} from "react";
import { NavigateFunction, useNavigate } from "react-router-dom";

// Define the context
interface ActiveMenuContextProps {
  activeMenu?: string;
  setActiveMenu: (menu: string) => void;
  expandedSubMenus: Record<string, boolean>;
  setExpandedSubMenus: Dispatch<SetStateAction<Record<string, boolean>>>;
  onMenuClick: (menu: AppMenuSchemaType, isSubMenu: boolean, appId: string) => void;
  navigate: NavigateFunction;
}

const ActiveMenuContext = createContext<ActiveMenuContextProps | undefined>(undefined);

interface ActiveMenuProviderProps {
  children: ReactNode;
}

const ActiveMenuProvider: React.FC<ActiveMenuProviderProps> = ({ children }) => {
  const [activeMenu, setActiveMenu] = useState<string>();
  const navigate = useNavigate();
  const [expandedSubMenus, setExpandedSubMenus] = useState<Record<string, boolean>>({});

  const onMenuClick = useCallback(
    (menu: AppMenuSchemaType, isSubMenu: boolean, appId: string) => {
      if (isSubMenu) {
        setExpandedSubMenus((prevState) => ({
          ...prevState,
          [menu._id as string]: !prevState[menu._id as string],
        }));
      }

      if (menu.callAnApi) {
        navigate(`/apps/${appId}/api-call/${menu._id}`);
      } else {
        const link = getLinkFromParams(`/apps/${appId}`, menu.params);
        if (link?.isExternalLink && link?.path) {
          window.open(link.path, "_blank", "noopener noreferrer");
        } else if (link?.path) {
          navigate(link.path);
        }
      }
    },
    [navigate, setExpandedSubMenus],
  );

  const value = useMemo(
    () => ({
      activeMenu,
      setActiveMenu,
      expandedSubMenus,
      setExpandedSubMenus,
      onMenuClick,
      navigate,
    }),
    [activeMenu, expandedSubMenus, navigate, onMenuClick],
  );

  return <ActiveMenuContext.Provider value={value}>{children}</ActiveMenuContext.Provider>;
};

// Custom hook to use the ActiveMenu context
const useActiveMenu = (): ActiveMenuContextProps => {
  const context = useContext(ActiveMenuContext);

  if (!context) {
    throw new Error("useActiveMenu must be used within an ActiveMenuProvider");
  }

  return context;
};

export { ActiveMenuProvider, useActiveMenu };
